import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, CheckCircle, Clock, Printer, Eye, IndianRupee, Home, User, Calendar, Trash2, Bell, AlertCircle, CreditCard, CalendarClock, Receipt, UserPlus, FileText, Edit2, MessageCircle, Phone, Mail, Globe, MapPin, Search, Filter, Download, FileSpreadsheet, Send, Upload, UserCheck } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { clearAppCache } from '@/utils/cacheUtils';

export default function Billing({ user, onLogout }) {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'bookings';
  const viewBookingId = searchParams.get('view');
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [installmentPlans, setInstallmentPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [quickCustomerOpen, setQuickCustomerOpen] = useState(false);
  const [viewBooking, setViewBooking] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBookingForPayment, setSelectedBookingForPayment] = useState(null);
  const [billingSettings, setBillingSettings] = useState(null);
  const printRef = useRef();
  const importFileRef = useRef(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [agentsList, setAgentsList] = useState([]);

  const [formData, setFormData] = useState({
    customer_id: '',
    property_id: '',
    payment_type: 'Full Payment',
    installments: [],
    agent_id: '',
    plot_no: ''
  });

  const [installmentForm, setInstallmentForm] = useState({
    installment_name: '',
    amount: '',
    due_date: ''
  });

  const [quickCustomerForm, setQuickCustomerForm] = useState({
    customer_name: '',
    mobile_number: '',
    address: '',
    relation_type: '',
    relation_name: ''
  });

  const [editBookingDialog, setEditBookingDialog] = useState(null);
  const [editBookingForm, setEditBookingForm] = useState({});
  const [plotFilter, setPlotFilter] = useState('');

  const openEditBooking = (booking) => {
    setEditBookingForm({
      plot_no: booking.plot_no || '',
      property_size: booking.property_size || '',
      address: booking.address || '',
      customer_name: booking.customer_name || '',
      mobile_number: booking.mobile_number || '',
      relation_type: booking.relation_type || '',
      relation_name: booking.relation_name || '',
      property_id: booking.property_id || '',
      property_name: booking.property_name || '',
      total_price: String(booking.total_price || ''),
      payment_type: booking.payment_type || 'Full Payment',
      agent_id: booking.agent_id || '',
      agent_name: booking.agent_name || '',
    });
    setEditBookingDialog(booking);
  };

  // Relation types for guardian/relative
  const relationTypes = [
    { value: 'father', label: 'Father (पिता)' },
    { value: 'husband', label: 'Husband (पति)' },
    { value: 'mother', label: 'Mother (माता)' },
    { value: 'wife', label: 'Wife (पत्नी)' },
    { value: 'son', label: 'Son (पुत्र)' },
    { value: 'daughter', label: 'Daughter (पुत्री)' },
    { value: 'brother', label: 'Brother (भाई)' },
    { value: 'sister', label: 'Sister (बहन)' },
    { value: 'guardian', label: 'Guardian (अभिभावक)' },
    { value: 'c/o', label: 'C/O (देखभाल)' },
    { value: 'other', label: 'Other (अन्य)' }
  ];

  const [paymentForm, setPaymentForm] = useState({
    installment_name: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    notes: '',
    custom_amount: '',
    use_custom_amount: false
  });

  const [sendingWhatsApp, setSendingWhatsApp] = useState({});
  const [expandedInstallment, setExpandedInstallment] = useState(null);

  // Search states
  const [customerSearch, setCustomerSearch] = useState('');
  const [bookingSearch, setBookingSearch] = useState('');
  
  // Billing list search and filter
  const [billingSearch, setBillingSearch] = useState('');
  const [billingMonthFilter, setBillingMonthFilter] = useState('all'); // 'all', '2026-01', '2026-02', etc.
  
  // Loading state for data
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [selectedBookingIds, setSelectedBookingIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [togglingNotif, setTogglingNotif] = useState(false);

  useEffect(() => {
    fetchData();
    fetchBillingSettings();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  // Auto-open booking from URL parameter
  useEffect(() => {
    if (viewBookingId && bookings.length > 0) {
      const booking = bookings.find(b => b.id === viewBookingId);
      if (booking) {
        setViewBooking(booking);
        // Clear the URL parameter using navigate
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('view');
        const newSearch = newParams.toString();
        window.history.replaceState({}, '', `/admin/billing${newSearch ? `?${newSearch}` : ''}`);
      }
    }
  }, [viewBookingId, bookings]);

  const fetchData = async () => {
    setIsDataLoading(true);
    try {
      const [bookingsRes, propertiesRes, customersRes, plansRes, notifRes, agentsRes] = await Promise.all([
        api.get('/bookings'),
        api.get('/properties'),
        api.get('/simple-customers'),
        api.get('/installment-plans'),
        api.get('/whatsapp/notification-status').catch(() => ({ data: { notifications_enabled: true } })),
        api.get('/agents').catch(() => ({ data: [] }))
      ]);
      setBookings(bookingsRes.data);
      setProperties(propertiesRes.data);
      setCustomers(customersRes.data);
      setInstallmentPlans(plansRes.data);
      setNotificationsEnabled(notifRes.data.notifications_enabled);
      setAgentsList(agentsRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsDataLoading(false);
    }
  };

  const fetchBillingSettings = async () => {
    try {
      const response = await api.get('/billing-settings');
      if (response.data.settings) {
        setBillingSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Failed to load billing settings');
    }
  };

  // Filtered customers for search
  const filteredCustomers = customers.filter(c => 
    c.customer_name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile_number.includes(customerSearch)
  );

  // Filtered bookings for payment search
  const filteredBookingsForPayment = bookings.filter(b => 
    b.payment_type === 'Installment' && 
    b.balance_due > 0 &&
    (b.customer_name.toLowerCase().includes(bookingSearch.toLowerCase()) ||
     b.booking_number.toLowerCase().includes(bookingSearch.toLowerCase()) ||
     b.mobile_number.includes(bookingSearch))
  );

  // Get available months from bookings for filter dropdown
  const getAvailableMonths = () => {
    const months = new Set();
    bookings.forEach(b => {
      const date = new Date(b.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months.add(monthKey);
    });
    return Array.from(months).sort().reverse();
  };

  // Filtered bookings for billing list (search + month filter + plot filter)
  const filteredBookings = bookings.filter(b => {
    // Search filter
    const searchMatch = billingSearch === '' || 
      b.customer_name.toLowerCase().includes(billingSearch.toLowerCase()) ||
      b.booking_number.toLowerCase().includes(billingSearch.toLowerCase()) ||
      b.property_name.toLowerCase().includes(billingSearch.toLowerCase()) ||
      b.mobile_number.includes(billingSearch) ||
      (b.plot_no || '').toLowerCase().includes(billingSearch.toLowerCase());
    
    // Month filter
    let monthMatch = true;
    if (billingMonthFilter !== 'all') {
      const bookingDate = new Date(b.created_at);
      const bookingMonth = `${bookingDate.getFullYear()}-${String(bookingDate.getMonth() + 1).padStart(2, '0')}`;
      monthMatch = bookingMonth === billingMonthFilter;
    }

    // Plot filter
    const plotMatch = plotFilter === '' || (b.plot_no || '').toLowerCase().includes(plotFilter.toLowerCase());
    
    return searchMatch && monthMatch && plotMatch;
  });

  // Get reminders data
  const getReminders = () => {
    const today = new Date();
    const reminders = {
      overdue: [],
      dueToday: [],
      dueThisWeek: [],
      dueThisMonth: [],
      dueNextMonth: []
    };

    bookings.forEach(booking => {
      if (booking.installments && booking.payment_type === 'Installment') {
        booking.installments.forEach(inst => {
          if (inst.status === 'Pending' || inst.status === 'Partially Paid') {
            const dueDate = new Date(inst.due_date);
            const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
            const dueMonth = dueDate.getMonth();
            const currentMonth = today.getMonth();
            const nextMonth = (currentMonth + 1) % 12;

            const reminderItem = {
              ...inst,
              bookingId: booking.id,
              bookingNumber: booking.booking_number,
              customerName: booking.customer_name,
              customerMobile: booking.mobile_number,
              propertyName: booking.property_name,
              diffDays
            };

            if (diffDays < 0) {
              reminders.overdue.push(reminderItem);
            } else if (diffDays === 0) {
              reminders.dueToday.push(reminderItem);
            } else if (diffDays <= 7) {
              reminders.dueThisWeek.push(reminderItem);
            } else if (dueMonth === currentMonth) {
              reminders.dueThisMonth.push(reminderItem);
            } else if (dueMonth === nextMonth) {
              reminders.dueNextMonth.push(reminderItem);
            }
          }
        });
      }
    });

    // Sort each category by due date
    Object.keys(reminders).forEach(key => {
      reminders[key].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
    });

    return reminders;
  };

  // Download Bookings CSV
  const downloadBookingsCSV = () => {
    const headers = ['Booking #', 'Customer', 'Mobile', 'Guardian', 'Property', 'Total Price', 'Paid', 'Balance', 'Payment Type', 'Status', 'Date'];
    const rows = filteredBookings.map(b => [
      b.booking_number,
      b.customer_name,
      b.mobile_number,
      `${b.relation_type || ''} ${b.relation_name || ''}`.trim() || '-',
      b.property_name,
      b.total_price,
      b.total_paid || 0,
      b.balance_due || 0,
      b.payment_type,
      b.payment_status,
      new Date(b.created_at).toLocaleDateString('en-IN')
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Bookings CSV downloaded!');
  };

  // Download Dues Sheet (customers with pending amounts)
  const downloadDuesSheet = () => {
    const duesData = bookings.filter(b => b.balance_due > 0);
    
    if (duesData.length === 0) {
      toast.error('No pending dues found!');
      return;
    }

    const headers = ['Booking #', 'Customer Name', 'Mobile', 'Property', 'Total Amount', 'Paid', 'Pending Amount', 'Payment Type', 'Date'];
    const rows = duesData.map(b => [
      b.booking_number,
      b.customer_name,
      b.mobile_number,
      b.property_name,
      b.total_price,
      b.total_paid || 0,
      b.balance_due,
      b.payment_type,
      new Date(b.created_at).toLocaleDateString('en-IN')
    ]);

    // Add total row
    const totalPending = duesData.reduce((sum, b) => sum + (b.balance_due || 0), 0);
    rows.push(['', '', '', '', '', 'TOTAL PENDING:', totalPending, '', '']);

    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dues_sheet_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Dues sheet downloaded! Total pending: ₹${totalPending.toLocaleString('en-IN')}`);
  };

  // Send Bulk WhatsApp Reminders
  const [sendingReminders, setSendingReminders] = useState(false);
  
  const sendBulkReminders = async () => {
    const duesCount = bookings.filter(b => b.balance_due > 0).length;
    if (duesCount === 0) {
      toast.error('कोई pending dues नहीं है!');
      return;
    }
    
    if (!window.confirm(`${duesCount} customers को WhatsApp reminder भेजना है?`)) {
      return;
    }
    
    setSendingReminders(true);
    try {
      const response = await api.post('/send-bulk-dues-reminder');
      toast.success(`✅ ${response.data.sent} reminders भेजे गए!`);
      if (response.data.failed > 0) {
        toast.warning(`⚠️ ${response.data.failed} failed`);
      }
    } catch (error) {
      toast.error('Reminders भेजने में error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingReminders(false);
    }
  };

  const handlePropertySelect = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    setSelectedProperty(property);
    setFormData({ ...formData, property_id: propertyId });
    
    // If a plan is already selected, regenerate installments with new property price
    if (selectedPlan && property) {
      applyInstallmentPlan(selectedPlan, property.total_price);
    }
  };

  const handlePlanSelect = (planId) => {
    const plan = installmentPlans.find(p => p.id === planId);
    setSelectedPlan(plan);
    
    if (plan && selectedProperty) {
      applyInstallmentPlan(plan, selectedProperty.total_price);
    }
  };

  const applyInstallmentPlan = (plan, totalPrice) => {
    const today = new Date();
    const installments = plan.installments.map(item => {
      const dueDate = new Date(today);
      dueDate.setMonth(dueDate.getMonth() + item.months_from_booking);
      
      let instAmount;
      if (plan.plan_type === 'amount') {
        instAmount = item.amount || 0;
      } else {
        instAmount = Math.round((item.percentage / 100) * totalPrice);
      }

      return {
        installment_name: item.installment_name,
        amount: instAmount,
        due_date: dueDate.toISOString(),
        status: 'Pending'
      };
    });
    
    setFormData(prev => ({
      ...prev,
      installments: installments
    }));
  };

  const handleQuickCustomerSubmit = async (e) => {
    e.preventDefault();
    if (!quickCustomerForm.customer_name || !quickCustomerForm.mobile_number) {
      toast.error('Please fill customer name and mobile number');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/simple-customers', quickCustomerForm);
      const newCustomer = response.data;
      
      // Add to customers list and select it
      setCustomers(prev => [...prev, newCustomer]);
      setFormData(prev => ({ ...prev, customer_id: newCustomer.id }));
      
      await clearAppCache();
      toast.success('Customer added successfully!');
      setQuickCustomerOpen(false);
      setQuickCustomerForm({ customer_name: '', mobile_number: '', address: '', relation_type: '', relation_name: '' });
    } catch (error) {
      toast.error('Failed to add customer');
    } finally {
      setLoading(false);
    }
  };

  const handleAddInstallment = () => {
    if (!installmentForm.installment_name || !installmentForm.amount || !installmentForm.due_date) {
      toast.error('Please fill all installment fields');
      return;
    }
    
    const newInstallment = {
      installment_name: installmentForm.installment_name,
      amount: parseFloat(installmentForm.amount),
      due_date: new Date(installmentForm.due_date).toISOString(),
      status: 'Pending'
    };
    
    setFormData({
      ...formData,
      installments: [...formData.installments, newInstallment]
    });
    
    setInstallmentForm({ installment_name: '', amount: '', due_date: '' });
  };

  const handleRemoveInstallment = (index) => {
    const updated = formData.installments.filter((_, i) => i !== index);
    setFormData({ ...formData, installments: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id || !formData.property_id) {
      toast.error('Please select customer and property');
      return;
    }
    
    if (formData.payment_type === 'Installment' && formData.installments.length === 0) {
      toast.error('Please add at least one installment');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/bookings', formData);
      await clearAppCache();
      toast.success('Booking created successfully!');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      property_id: '',
      payment_type: 'Full Payment',
      installments: [],
      agent_id: '',
      plot_no: ''
    });
    setSelectedProperty(null);
    setSelectedPlan(null);
    setInstallmentForm({ installment_name: '', amount: '', due_date: '' });
  };

  const handleEditBooking = async () => {
    if (!editBookingDialog) return;
    try {
      await api.put(`/bookings/${editBookingDialog.id}`, editBookingForm);
      toast.success('Booking updated!');
      setEditBookingDialog(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  const handleMarkPaid = async (bookingId, installmentName) => {
    try {
      await api.patch(`/bookings/${bookingId}/installment`, null, {
        params: {
          installment_name: installmentName,
          status: 'Paid',
          paid_date: new Date().toISOString()
        }
      });
      await clearAppCache();
      toast.success('Installment marked as paid!');
      fetchData();
      if (viewBooking) {
        const res = await api.get(`/bookings/${bookingId}`);
        setViewBooking(res.data);
      }
    } catch (error) {
      toast.error('Failed to update installment');
    }
  };

  const handleSendWhatsApp = async (bookingId, installmentName) => {
    const key = `${bookingId}-${installmentName}`;
    setSendingWhatsApp(prev => ({ ...prev, [key]: true }));
    
    try {
      // Send base URL with request so backend uses correct domain for invoice link
      const baseUrl = window.location.origin;
      const response = await api.post(
        `/send-whatsapp-reminder/${bookingId}/${encodeURIComponent(installmentName)}`,
        { base_url: baseUrl }
      );
      toast.success('WhatsApp reminder bhej diya gaya! 📱');
    } catch (error) {
      const detail = error.response?.data?.detail || 'WhatsApp send nahi hua';
      if (detail.includes('not configured')) {
        toast.error('WhatsApp configure nahi hai. Admin → WhatsApp mein settings karein.');
      } else {
        toast.error(detail);
      }
    } finally {
      setSendingWhatsApp(prev => ({ ...prev, [key]: false }));
    }
  };

  const toggleBookingSelect = (id) => {
    setSelectedBookingIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleSelectAllBookings = () => {
    if (selectedBookingIds.size === filteredBookings.length) {
      setSelectedBookingIds(new Set());
    } else {
      setSelectedBookingIds(new Set(filteredBookings.map(b => b.id)));
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Kya aap yeh booking delete karna chahte hain?')) return;
    try {
      await api.delete(`/bookings/${bookingId}`);
      await clearAppCache();
      toast.success('Booking deleted!');
      setSelectedBookingIds(prev => { const n = new Set(prev); n.delete(bookingId); return n; });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const handleBulkDeleteBookings = async () => {
    if (selectedBookingIds.size === 0) return;
    if (!window.confirm(`Kya aap ${selectedBookingIds.size} bookings delete karna chahte hain? Yeh action undo nahi hoga!`)) return;
    setBulkDeleting(true);
    try {
      await api.post('/bookings/bulk-delete', { ids: Array.from(selectedBookingIds) });
      await clearAppCache();
      toast.success(`${selectedBookingIds.size} bookings deleted!`);
      setSelectedBookingIds(new Set());
      fetchData();
    } catch (error) {
      toast.error('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDownloadImportTemplate = async () => {
    try {
      const response = await api.get('/bookings/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'booking_import_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Blank template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleDownloadSampleFile = async () => {
    try {
      const response = await api.get('/bookings/sample-import', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sample_booking_data.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Sample file downloaded!');
    } catch (error) {
      toast.error('Failed to download sample');
    }
  };

  const toggleNotifications = async () => {
    setTogglingNotif(true);
    try {
      const res = await api.post('/whatsapp/toggle-notifications');
      setNotificationsEnabled(res.data.notifications_enabled);
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Failed to toggle notifications');
    } finally {
      setTogglingNotif(false);
    }
  };

  const handleImportBookingsFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/bookings/bulk-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      if (response.data.bookings_created > 0) {
        toast.success(`${response.data.bookings_created} bookings imported!`);
        await clearAppCache();
        fetchData();
      }
      setImportDialogOpen(true);
    } catch (error) {
      toast.error('Import failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
      if (importFileRef.current) importFileRef.current.value = '';
    }
  };

  const openPaymentDialog = (booking) => {
    setSelectedBookingForPayment(booking);
    const unpaidInstallments = booking.installments?.filter(i => i.status !== 'Paid') || [];
    if (unpaidInstallments.length > 0) {
      const inst = unpaidInstallments[0];
      const remaining = (inst.amount || 0) - (inst.paid_amount || 0);
      setPaymentForm({
        ...paymentForm,
        installment_name: inst.installment_name,
        custom_amount: remaining > 0 ? String(remaining) : String(inst.amount || 0),
        use_custom_amount: false
      });
    }
    setPaymentDialogOpen(true);
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!paymentForm.use_custom_amount && !paymentForm.installment_name) {
      toast.error('Please select an installment or enter custom amount');
      return;
    }
    
    const payAmount = paymentForm.use_custom_amount 
      ? parseFloat(paymentForm.custom_amount)
      : parseFloat(paymentForm.custom_amount || 0);
    
    if (!payAmount || payAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setLoading(true);
    try {
      if (paymentForm.use_custom_amount) {
        // Use direct payment API for custom amount (not tied to installment)
        await api.put(`/bookings/${selectedBookingForPayment.id}/payment`, {
          action: 'add_payment',
          amount: payAmount,
          payment_mode: paymentForm.payment_method,
          remarks: paymentForm.notes
        });
      } else {
        // Use new partial payment API
        await api.post(`/bookings/${selectedBookingForPayment.id}/record-installment-payment`, {
          installment_name: paymentForm.installment_name,
          amount: payAmount,
          payment_date: new Date(paymentForm.payment_date).toISOString(),
          payment_mode: paymentForm.payment_method,
          notes: paymentForm.notes
        });
      }
      
      await clearAppCache();
      toast.success('Payment recorded successfully!');
      setPaymentDialogOpen(false);
      setPaymentForm({
        installment_name: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        notes: '',
        custom_amount: '',
        use_custom_amount: false
      });
      fetchData();
      if (viewBooking) {
        const res = await api.get(`/bookings/${selectedBookingForPayment.id}`);
        setViewBooking(res.data);
      }
    } catch (error) {
      toast.error('Failed to record payment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const settings = billingSettings || {};
    const booking = viewBooking;
    const invoiceDate = new Date(booking.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const paidPct = booking.total_price > 0 ? Math.round((booking.total_paid / booking.total_price) * 100) : 0;
    const relationText = booking.relation_type && booking.relation_name 
      ? `${booking.relation_type === 'father' ? 'S/o' : booking.relation_type === 'husband' ? 'W/o' : booking.relation_type === 'wife' ? 'H/o' : booking.relation_type === 'mother' ? 'D/o' : booking.relation_type === 'c/o' ? 'C/o' : booking.relation_type === 'guardian' ? 'G/o' : 'R/o'} ${booking.relation_name}` 
      : '';

    const installmentsHTML = booking.installments && booking.installments.length > 0 ? `
      <table class="inst-table">
        <thead>
          <tr>
            <th style="width:5%">#</th>
            <th style="width:26%">Description</th>
            <th style="text-align:right;width:16%">Amount (Rs)</th>
            <th style="text-align:right;width:16%">Paid (Rs)</th>
            <th style="text-align:center;width:14%">Due Date</th>
            <th style="text-align:center;width:14%">Paid Date</th>
            <th style="text-align:center;width:15%">Status</th>
          </tr>
        </thead>
        <tbody>
          ${booking.installments.map((inst, i) => {
            const lastPayment = (inst.payment_history && inst.payment_history.length > 0) ? inst.payment_history[inst.payment_history.length - 1] : null;
            const paidDateStr = inst.paid_date || (lastPayment ? lastPayment.payment_date : null);
            return `
            <tr>
              <td class="sn">${i+1}</td>
              <td class="desc">${inst.installment_name}</td>
              <td class="amt">${(inst.amount || 0).toLocaleString('en-IN')}</td>
              <td class="amt" style="color:${(inst.paid_amount || 0) > 0 ? '#16a34a' : '#9ca3af'}">${(inst.paid_amount || 0).toLocaleString('en-IN')}</td>
              <td class="dt">${inst.due_date ? new Date(inst.due_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '-'}</td>
              <td class="dt" style="color:#16a34a">${paidDateStr ? new Date(paidDateStr).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '-'}</td>
              <td class="st"><span class="tag ${inst.status === 'Paid' ? 'tag-g' : inst.status === 'Partially Paid' ? 'tag-b' : 'tag-o'}">${inst.status === 'Paid' ? 'PAID' : inst.status === 'Partially Paid' ? 'PARTIAL' : 'PENDING'}</span></td>
            </tr>
          `}).join('')}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="2" class="tf">TOTAL</td>
            <td class="amt tf">${booking.total_price.toLocaleString('en-IN')}</td>
            <td class="amt tf" style="color:#16a34a">${(booking.total_paid || 0).toLocaleString('en-IN')}</td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    ` : '';

    const bankItems = [];
    if (settings.bank_name) bankItems.push(`<span class="bk"><b>Bank:</b> ${settings.bank_name}</span>`);
    if (settings.account_number) bankItems.push(`<span class="bk"><b>A/C:</b> ${settings.account_number}</span>`);
    if (settings.ifsc_code) bankItems.push(`<span class="bk"><b>IFSC:</b> ${settings.ifsc_code}</span>`);
    if (settings.upi_id) bankItems.push(`<span class="bk"><b>UPI:</b> ${settings.upi_id}</span>`);
    const bankHTML = booking.balance_due > 0 && bankItems.length > 0 ? `
      <div class="bank-row">
        <div class="bank-label">PAYMENT INFO</div>
        <div class="bank-vals">${bankItems.join('<span class="sep">|</span>')}</div>
      </div>
    ` : '';

    const termsHTML = settings.terms_and_conditions ? `
      <div class="terms"><b>Terms:</b> ${settings.terms_and_conditions.replace(/\n/g, ' | ')}</div>
    ` : '';

    printWindow.document.write(`<!DOCTYPE html>
<html><head>
<title>Invoice - ${booking.booking_number}</title>
<meta charset="UTF-8">
<style>
  @page { size: A4; margin: 12mm 10mm 10mm 10mm; }
  * { margin:0; padding:0; box-sizing:border-box; -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; }
  body { font-family: 'Segoe UI', Tahoma, Geneva, sans-serif; color: #000000; font-size: 12px; line-height: 1.5; }
  .page { width: 100%; max-width: 210mm; margin: 0 auto; }

  /* === HEADER === */
  .hdr { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 10px; border-bottom: 3px solid #0B1120; }
  .hdr-left { display: flex; align-items: center; gap: 10px; }
  .hdr-left img { height: 48px; width: auto; }
  .co-name { font-size: 18px; font-weight: 800; color: #0B1120; letter-spacing: -0.2px; line-height: 1.1; }
  .co-tag { font-size: 9px; color: #0B1120; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; }
  .co-addr { font-size: 9px; color: #000000; margin-top: 2px; line-height: 1.4; }
  .hdr-right { text-align: right; }
  .inv-title { font-size: 24px; font-weight: 900; color: #0B1120; letter-spacing: 4px; line-height: 1; }
  .inv-no { font-size: 13px; font-weight: 700; color: #0B1120; font-family: 'Courier New', monospace; margin-top: 2px; }
  .inv-meta { font-size: 10px; color: #000000; margin-top: 1px; font-weight: 600; }

  /* === GOLD STRIP === */
  .gold-strip { height: 3px; background: linear-gradient(90deg, #C9A84C 0%, #e8d48b 50%, #C9A84C 100%); margin: 0; }

  /* === DETAILS ROW === */
  .details { display: flex; gap: 16px; margin: 12px 0 10px; }
  .det-box { flex: 1; padding: 10px 12px; border: 1px solid #e5e7eb; border-radius: 6px; }
  .det-box.prop { background: #fafaf5; border-color: #d4c88a; }
  .det-lbl { font-size: 9px; font-weight: 700; color: #0B1120; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 5px; }
  .det-name { font-size: 15px; font-weight: 700; color: #000000; line-height: 1.2; }
  .det-sub { font-size: 10px; color: #000000; margin-top: 2px; font-weight: 500; }
  .det-price { font-size: 17px; font-weight: 800; color: #000000; margin-top: 4px; }
  .det-price small { font-size: 10px; color: #333333; font-weight: 500; margin-left: 4px; }

  /* === SUMMARY BAR === */
  .sum-bar { display: flex; border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; margin-bottom: 12px; }
  .sum-item { flex: 1; padding: 8px 10px; text-align: center; border-right: 1px solid #e5e7eb; }
  .sum-item:last-child { border-right: none; }
  .sum-item .sl { font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 2px; color: #000000; }
  .sum-item .sv { font-size: 15px; font-weight: 800; color: #000000; }
  .s-type { background: #f9fafb; }
  .s-type .sl { color: #000000; } .s-type .sv { color: #000000; font-size: 12px; }
  .s-paid { background: #f0fdf4; }
  .s-paid .sl { color: #16a34a; } .s-paid .sv { color: #16a34a; }
  .s-due { background: #fef2f2; }
  .s-due .sl { color: #dc2626; } .s-due .sv { color: #dc2626; }
  .s-pct { background: #fffbeb; }
  .s-pct .sl { color: #a16207; } .s-pct .sv { color: #a16207; }

  /* === INSTALLMENT TABLE === */
  .inst-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
  .inst-table thead tr { background: #0B1120; }
  .inst-table th { padding: 7px 8px; font-size: 9px; font-weight: 700; color: #FFD700; text-transform: uppercase; letter-spacing: 0.6px; text-align: left; }
  .inst-table td { padding: 6px 8px; font-size: 11px; border-bottom: 1px solid #e5e7eb; color: #000000; }
  .inst-table .sn { color: #333333; font-weight: 600; text-align: center; }
  .inst-table .desc { font-weight: 700; color: #000000; }
  .inst-table .amt { text-align: right; font-weight: 700; font-family: 'Courier New', monospace; color: #000000; }
  .inst-table .dt { text-align: center; color: #000000; font-weight: 500; }
  .inst-table .st { text-align: center; }
  .inst-table .tf { font-weight: 800; background: #f0f0f0; border-top: 2px solid #0B1120; padding: 7px 8px; color: #000000; }
  .inst-table tfoot td { font-size: 11px; color: #000000; }
  .tag { display: inline-block; padding: 2px 10px; border-radius: 10px; font-size: 9px; font-weight: 700; letter-spacing: 0.3px; }
  .tag-g { background: #dcfce7; color: #166534; }
  .tag-o { background: #fef3c7; color: #92400e; }
  .tag-b { background: #dbeafe; color: #1e40af; }

  /* === PAID STAMP === */
  .paid-stamp { text-align: center; margin: 14px 0; }
  .paid-circle { display: inline-block; width: 80px; height: 80px; border: 3px solid #16a34a; border-radius: 50%; line-height: 74px; font-size: 18px; font-weight: 900; color: #16a34a; letter-spacing: 2px; transform: rotate(-12deg); opacity: 0.6; }

  /* === BANK ROW === */
  .bank-row { background: #f5f5f5; border: 1px solid #d0d0d0; border-radius: 5px; padding: 7px 10px; margin-bottom: 8px; display: flex; align-items: center; gap: 10px; }
  .bank-label { font-size: 8px; font-weight: 700; color: #000000; text-transform: uppercase; letter-spacing: 1px; white-space: nowrap; }
  .bank-vals { font-size: 10px; color: #000000; display: flex; flex-wrap: wrap; align-items: center; gap: 2px; }
  .bk { white-space: nowrap; }
  .bk b { color: #333333; font-weight: 700; }
  .sep { color: #999999; margin: 0 6px; }

  /* === TERMS === */
  .terms { font-size: 10px; color: #000000; line-height: 1.6; margin-bottom: 8px; }
  .terms b { color: #000000; font-weight: 700; }

  /* === SIGNATURES === */
  .sign-row { display: flex; justify-content: space-between; margin: 18px 0 10px; padding-top: 8px; }
  .sign-box { text-align: center; width: 40%; }
  .sign-line { height: 1px; background: #333333; margin-bottom: 4px; margin-top: 30px; }
  .sign-lbl { font-size: 10px; color: #000000; font-weight: 700; }
  .sign-nm { font-size: 9px; color: #333333; font-weight: 500; }

  /* === FOOTER === */
  .ftr { background: #0B1120; padding: 8px 14px; display: flex; justify-content: space-between; align-items: center; border-radius: 0 0 6px 6px; margin-top: 6px; }
  .ftr-l { color: #FFD700; font-size: 11px; font-weight: 700; }
  .ftr-l span { display: block; color: #cccccc; font-size: 9px; font-weight: 400; margin-top: 1px; }
  .ftr-r { text-align: right; color: #cccccc; font-size: 9px; line-height: 1.4; }
  .ftr-r a { color: #FFD700; text-decoration: none; }
</style>
</head>
<body>
<div class="page">
  <!-- Header -->
  <div class="hdr">
    <div class="hdr-left">
      <img src="https://ganesahomestate.com/images/logo.png" alt="Logo" onerror="this.style.display='none'" />
      <div>
        <div class="co-name">${settings.company_name || 'Ganesa Homestate Pvt. Ltd.'}</div>
        <div class="co-tag">${settings.company_tagline || 'Tradition of Trust'}</div>
        ${settings.address_line1 ? `<div class="co-addr">${settings.address_line1}${settings.city ? ', ' + settings.city : ''}${settings.state ? ', ' + settings.state : ''}${settings.pincode ? ' - ' + settings.pincode : ''}<br>${settings.phone ? 'Ph: ' + settings.phone : ''}${settings.email ? ' | ' + settings.email : ''}</div>` : ''}
      </div>
    </div>
    <div class="hdr-right">
      <div class="inv-title">INVOICE</div>
      <div class="inv-no">${booking.booking_number}</div>
      <div class="inv-meta">${invoiceDate}</div>
      ${settings.gst_number ? `<div class="inv-meta">GSTIN: ${settings.gst_number}</div>` : ''}
    </div>
  </div>
  <div class="gold-strip"></div>

  <!-- Details -->
  <div class="details">
    <div class="det-box">
      <div class="det-lbl">Bill To</div>
      <div class="det-name">${booking.customer_name}</div>
      ${relationText ? `<div class="det-sub">${relationText}</div>` : ''}
      <div class="det-sub">${booking.mobile_number}</div>
      ${booking.address ? `<div class="det-sub">${booking.address}</div>` : ''}
    </div>
    <div class="det-box prop">
      <div class="det-lbl">Property Details</div>
      <div class="det-name">${booking.property_name}</div>
      <div class="det-sub">Size: ${booking.property_size || '-'}</div>
      ${booking.plot_no ? `<div class="det-sub" style="color:#c2410c;font-weight:700;margin-top:4px;background:#fff7ed;display:inline-block;padding:2px 8px;border-radius:4px;border:1px solid #fed7aa">Plot No: ${booking.plot_no}</div>` : ''}
      <div class="det-price">${booking.total_price.toLocaleString('en-IN')} <small>Total Value</small></div>
    </div>
  </div>

  <!-- Summary -->
  <div class="sum-bar">
    <div class="sum-item s-type"><div class="sl">Payment Type</div><div class="sv">${booking.payment_type}</div></div>
    <div class="sum-item s-paid"><div class="sl">Amount Paid</div><div class="sv">${booking.total_paid.toLocaleString('en-IN')}</div></div>
    <div class="sum-item s-due"><div class="sl">Balance Due</div><div class="sv">${booking.balance_due.toLocaleString('en-IN')}</div></div>
    <div class="sum-item s-pct"><div class="sl">Progress</div><div class="sv">${paidPct}%</div></div>
  </div>

  ${booking.payment_type === 'Full Payment' ? `<div class="paid-stamp"><div class="paid-circle">PAID</div></div>` : ''}

  <!-- Installments -->
  ${installmentsHTML}

  <!-- Bank -->
  ${bankHTML}

  <!-- Terms -->
  ${termsHTML}

  <!-- Signatures -->
  <div class="sign-row">
    <div class="sign-box"><div class="sign-line"></div><div class="sign-lbl">Customer Signature</div><div class="sign-nm">${booking.customer_name}</div></div>
    <div class="sign-box"><div class="sign-line"></div><div class="sign-lbl">Authorized Signatory</div><div class="sign-nm">${settings.company_name || 'Ganesa Homestate'}</div></div>
  </div>

  <!-- Footer -->
  <div class="ftr">
    <div class="ftr-l">${settings.company_name || 'Ganesa Homestate Pvt. Ltd.'}<span>${settings.company_tagline || 'Tradition of Trust'}</span></div>
    <div class="ftr-r">Thank you for your trust!<br>Computer generated invoice${settings.website ? ` | <a href="${settings.website}">${settings.website}</a>` : ''}</div>
  </div>
</div>
</body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 300);
  };

  const getStatusBadge = (status) => {
    if (status === 'Fully Paid') {
      return <Badge className="bg-green-100 text-green-700 border-green-200">Fully Paid</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Partially Paid</Badge>;
  };

  const getInstallmentStatusBadge = (status) => {
    if (status === 'Paid') {
      return <Badge className="bg-green-100 text-green-700 border-green-200 gap-1"><CheckCircle size={12} /> Paid</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1"><Clock size={12} /> Pending</Badge>;
  };

  const totalInstallmentAmount = formData.installments.reduce((sum, inst) => sum + inst.amount, 0);
  const reminders = getReminders();
  const totalPendingReminders = reminders.overdue.length + reminders.dueToday.length + reminders.dueThisWeek.length;

  const ReminderCard = ({ item, showMarkPaid = true }) => (
    <div className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-colors gap-4">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl ${item.diffDays < 0 ? 'bg-red-100' : item.diffDays === 0 ? 'bg-amber-100' : 'bg-blue-100'}`}>
          {item.diffDays < 0 ? (
            <AlertCircle size={20} className="text-red-600" />
          ) : (
            <Calendar size={20} className={item.diffDays === 0 ? 'text-amber-600' : 'text-blue-600'} />
          )}
        </div>
        <div>
          <p className="font-semibold text-slate-800">{item.customerName}</p>
          <p className="text-sm text-slate-500">{item.propertyName}</p>
          <p className="text-xs text-slate-400">{item.installment_name}</p>
          <p className="text-xs text-slate-400">📱 {item.customerMobile}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-lg font-bold text-slate-800">₹{item.amount.toLocaleString('en-IN')}</p>
        <p className={`text-sm font-medium ${item.diffDays < 0 ? 'text-red-600' : item.diffDays === 0 ? 'text-amber-600' : 'text-blue-600'}`}>
          {item.diffDays < 0 
            ? `${Math.abs(item.diffDays)} days overdue` 
            : item.diffDays === 0 
              ? 'Due Today' 
              : `Due in ${item.diffDays} days`}
        </p>
        <p className="text-xs text-slate-400">{new Date(item.due_date).toLocaleDateString('en-IN')}</p>
      </div>
      {showMarkPaid && (
        <div className="flex flex-col md:flex-row gap-2 md:ml-4">
          <Button
            size="sm"
            variant="outline"
            data-testid={`whatsapp-${item.bookingId}-${item.installment_name}`}
            onClick={() => handleSendWhatsApp(item.bookingId, item.installment_name)}
            disabled={sendingWhatsApp[`${item.bookingId}-${item.installment_name}`]}
            className="gap-1 text-green-600 border-green-200 hover:bg-green-50"
          >
            {sendingWhatsApp[`${item.bookingId}-${item.installment_name}`] ? (
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600"></div>
            ) : (
              <MessageCircle size={14} />
            )}
            WhatsApp
          </Button>
          <Button
            size="sm"
            onClick={() => handleMarkPaid(item.bookingId, item.installment_name)}
            className="bg-emerald-600 hover:bg-emerald-700 gap-1"
          >
            <CheckCircle size={14} />
            Record Payment
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="billing-page">
        {/* Header */}
        <Card data-testid="billing-header-card">
          <CardHeader>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-2xl">Billing & Bookings</CardTitle>
                <p className="text-sm text-slate-500 mt-1">Manage property bookings, installments and payments</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {/* Download Buttons */}
                <Button variant="outline" onClick={downloadBookingsCSV} className="gap-2" data-testid="download-bookings-btn">
                  <Download size={16} />
                  Export All
                </Button>
                <Button variant="outline" onClick={downloadDuesSheet} className="gap-2 text-amber-600 border-amber-300 hover:bg-amber-50" data-testid="download-dues-btn">
                  <FileSpreadsheet size={16} />
                  Dues Sheet
                </Button>
                <Button 
                  variant="outline" 
                  onClick={sendBulkReminders} 
                  disabled={sendingReminders}
                  className="gap-2 text-green-600 border-green-300 hover:bg-green-50" 
                  data-testid="send-reminders-btn"
                >
                  <Send size={16} />
                  {sendingReminders ? 'Sending...' : 'WhatsApp Reminder'}
                </Button>
                
                {/* Import Bookings */}
                <Button variant="outline" onClick={handleDownloadImportTemplate} className="gap-2 text-blue-600 border-blue-300 hover:bg-blue-50" data-testid="download-import-template">
                  <Download size={16} />
                  Blank Template
                </Button>
                <Button variant="outline" onClick={handleDownloadSampleFile} className="gap-2 text-teal-600 border-teal-300 hover:bg-teal-50" data-testid="download-sample-file">
                  <FileSpreadsheet size={16} />
                  Sample Data
                </Button>
                <Button
                  variant="outline"
                  onClick={() => importFileRef.current?.click()}
                  disabled={importing}
                  className="gap-2 text-purple-600 border-purple-300 hover:bg-purple-50"
                  data-testid="import-bookings-btn"
                >
                  <Upload size={16} />
                  {importing ? 'Importing...' : 'Import Bookings'}
                </Button>
                <input
                  type="file"
                  ref={importFileRef}
                  accept=".csv"
                  onChange={handleImportBookingsFile}
                  className="hidden"
                />
                {/* WhatsApp Notification Toggle */}
                {(user?.role === 'admin' || user?.role === 'billing') && (
                  <Button
                    variant={notificationsEnabled ? "outline" : "destructive"}
                    onClick={toggleNotifications}
                    disabled={togglingNotif}
                    className={`gap-2 ${notificationsEnabled ? 'text-green-600 border-green-300 hover:bg-green-50' : ''}`}
                    data-testid="toggle-whatsapp-notifications"
                  >
                    <Bell size={16} />
                    {togglingNotif ? '...' : notificationsEnabled ? 'WA Auto: ON' : 'WA Auto: OFF'}
                  </Button>
                )}

                <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="record-payment-button" className="gap-2">
                      <CreditCard size={18} />
                      Record Payment
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="payment-dialog">
                    <DialogHeader>
                      <DialogTitle>Record Payment</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleRecordPayment} className="space-y-4">
                      {/* Search Booking */}
                      <div className="space-y-2">
                        <Label>Search Booking</Label>
                        <Input
                          data-testid="booking-search-input"
                          placeholder="Search by customer name, mobile or booking number..."
                          value={bookingSearch}
                          onChange={(e) => setBookingSearch(e.target.value)}
                        />
                      </div>

                      {/* Select Booking */}
                      <div className="space-y-2">
                        <Label>Select Booking *</Label>
                        <select
                          data-testid="payment-booking-select"
                          value={selectedBookingForPayment?.id || ''}
                          onChange={(e) => {
                            const booking = bookings.find(b => b.id === e.target.value);
                            if (booking) {
                              openPaymentDialog(booking);
                            }
                          }}
                          required
                          className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="">Choose Booking</option>
                          {filteredBookingsForPayment.map(booking => (
                            <option key={booking.id} value={booking.id}>
                              {booking.booking_number} - {booking.customer_name} (Balance: ₹{booking.balance_due.toLocaleString('en-IN')})
                            </option>
                          ))}
                        </select>
                        {bookingSearch && filteredBookingsForPayment.length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">No booking found with pending installments.</p>
                        )}
                      </div>

                      {/* Booking Summary */}
                      {selectedBookingForPayment && (
                        <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                          <p className="text-sm"><span className="text-slate-500">Customer:</span> <span className="font-semibold">{selectedBookingForPayment.customer_name}</span></p>
                          <p className="text-sm"><span className="text-slate-500">Property:</span> <span className="font-semibold">{selectedBookingForPayment.property_name}</span></p>
                          <p className="text-sm"><span className="text-slate-500">Total:</span> <span className="font-semibold">₹{selectedBookingForPayment.total_price?.toLocaleString('en-IN') || '0'}</span></p>
                          <p className="text-lg font-bold text-red-600">Balance Due: ₹{(selectedBookingForPayment.balance_due || 0).toLocaleString('en-IN')}</p>
                        </div>
                      )}

                      {/* Payment Type Toggle */}
                      {selectedBookingForPayment && (
                        <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                          <button
                            type="button"
                            onClick={() => setPaymentForm({ ...paymentForm, use_custom_amount: false })}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                              !paymentForm.use_custom_amount 
                                ? 'bg-white shadow text-emerald-700' 
                                : 'text-slate-600 hover:text-slate-800'
                            }`}
                          >
                            Installment Select करें
                          </button>
                          <button
                            type="button"
                            onClick={() => setPaymentForm({ ...paymentForm, use_custom_amount: true })}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                              paymentForm.use_custom_amount 
                                ? 'bg-white shadow text-emerald-700' 
                                : 'text-slate-600 hover:text-slate-800'
                            }`}
                          >
                            Custom Amount
                          </button>
                        </div>
                      )}

                      {/* Select Installment */}
                      {selectedBookingForPayment && !paymentForm.use_custom_amount && (
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label>Select Installment *</Label>
                            <select
                              data-testid="payment-installment-select"
                              value={paymentForm.installment_name}
                              onChange={(e) => {
                                const inst = selectedBookingForPayment.installments?.find(i => i.installment_name === e.target.value);
                                const remaining = inst ? (inst.amount || 0) - (inst.paid_amount || 0) : 0;
                                setPaymentForm({ ...paymentForm, installment_name: e.target.value, custom_amount: String(remaining > 0 ? remaining : inst?.amount || 0) });
                              }}
                              required={!paymentForm.use_custom_amount}
                              className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            >
                              <option value="">Choose Installment</option>
                              {selectedBookingForPayment.installments?.filter(i => i.status !== 'Paid').map((inst, idx) => {
                                const remaining = (inst.amount || 0) - (inst.paid_amount || 0);
                                return (
                                  <option key={idx} value={inst.installment_name}>
                                    {inst.installment_name} - ₹{inst.amount?.toLocaleString('en-IN')} 
                                    {inst.paid_amount > 0 ? ` (Paid: ₹${inst.paid_amount?.toLocaleString('en-IN')}, Remaining: ₹${remaining.toLocaleString('en-IN')})` : ''} 
                                    (Due: {new Date(inst.due_date).toLocaleDateString('en-IN')})
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                          {/* Installment amount info + partial payment input */}
                          {paymentForm.installment_name && (() => {
                            const inst = selectedBookingForPayment.installments?.find(i => i.installment_name === paymentForm.installment_name);
                            if (!inst) return null;
                            const remaining = (inst.amount || 0) - (inst.paid_amount || 0);
                            return (
                              <div className="space-y-2">
                                {inst.paid_amount > 0 && (
                                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                    <p className="text-sm font-medium text-amber-800">Partial Payment Pending</p>
                                    <p className="text-xs text-amber-600 mt-1">
                                      Installment: ₹{inst.amount?.toLocaleString('en-IN')} | 
                                      Paid: ₹{inst.paid_amount?.toLocaleString('en-IN')} | 
                                      Remaining: <span className="font-bold">₹{remaining.toLocaleString('en-IN')}</span>
                                    </p>
                                  </div>
                                )}
                                <div>
                                  <Label>Payment Amount (₹) *</Label>
                                  <Input
                                    type="number"
                                    data-testid="installment-payment-amount"
                                    placeholder={`Enter amount (Max: ₹${remaining.toLocaleString('en-IN')})`}
                                    value={paymentForm.custom_amount}
                                    onChange={(e) => setPaymentForm({ ...paymentForm, custom_amount: e.target.value })}
                                    required
                                    min="1"
                                    className="text-lg font-semibold"
                                  />
                                  <p className="text-xs text-slate-500 mt-1">
                                    Full amount ya partial amount enter karein (Remaining: ₹{remaining.toLocaleString('en-IN')})
                                  </p>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Custom Amount Input */}
                      {selectedBookingForPayment && paymentForm.use_custom_amount && (
                        <div className="space-y-2">
                          <Label>Enter Amount (₹) *</Label>
                          <Input
                            type="number"
                            data-testid="custom-amount-input"
                            placeholder="Enter payment amount"
                            value={paymentForm.custom_amount}
                            onChange={(e) => setPaymentForm({ ...paymentForm, custom_amount: e.target.value })}
                            required={paymentForm.use_custom_amount}
                            min="1"
                            className="text-lg font-semibold"
                          />
                          <p className="text-xs text-slate-500">
                            कोई भी amount enter करें (Balance: ₹{(selectedBookingForPayment.balance_due || 0).toLocaleString('en-IN')})
                          </p>
                        </div>
                      )}

                      {/* Payment Date */}
                      <div className="space-y-2">
                        <Label>Payment Date *</Label>
                        <Input
                          type="date"
                          data-testid="payment-date-input"
                          value={paymentForm.payment_date}
                          onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                          required
                        />
                      </div>

                      {/* Payment Method */}
                      <div className="space-y-2">
                        <Label>Payment Method</Label>
                        <select
                          data-testid="payment-method-select"
                          value={paymentForm.payment_method}
                          onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                          className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="cash">Cash</option>
                          <option value="cheque">Cheque</option>
                          <option value="upi">UPI</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="card">Card</option>
                        </select>
                      </div>

                      {/* Notes */}
                      <div className="space-y-2">
                        <Label>Notes (Optional)</Label>
                        <Input
                          data-testid="payment-notes-input"
                          placeholder="Transaction ID, Cheque No., etc."
                          value={paymentForm.notes}
                          onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                        />
                      </div>

                      <Button
                        type="submit"
                        data-testid="submit-payment-btn"
                        disabled={loading || !selectedBookingForPayment}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
                      >
                        {loading ? 'Recording...' : 'Record Payment'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                  <DialogTrigger asChild>
                    <Button data-testid="new-booking-button" className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                      <Plus size={18} />
                      New Booking
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="booking-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Booking</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Customer Selection with Quick Add */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-2">
                            <User size={16} className="text-emerald-600" />
                            Select Customer *
                          </Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            data-testid="quick-add-customer-btn"
                            onClick={() => setQuickCustomerOpen(true)}
                            className="text-emerald-600 hover:text-emerald-700 gap-1 h-8"
                          >
                            <UserPlus size={14} />
                            Quick Add
                          </Button>
                        </div>
                        {/* Customer Search */}
                        <Input
                          data-testid="customer-search-input"
                          placeholder="Search customer by name or mobile..."
                          value={customerSearch}
                          onChange={(e) => setCustomerSearch(e.target.value)}
                          className="mb-2"
                        />
                        <select
                          data-testid="customer-select"
                          value={formData.customer_id}
                          onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                          required
                          className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="">Choose Customer</option>
                          {filteredCustomers.map(customer => (
                            <option key={customer.id} value={customer.id}>
                              {customer.customer_name} - {customer.mobile_number}
                            </option>
                          ))}
                        </select>
                        {customerSearch && filteredCustomers.length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">No customer found. Use Quick Add to create new.</p>
                        )}
                      </div>

                      {/* Quick Add Customer Mini Dialog */}
                      {quickCustomerOpen && (
                        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-blue-800">Add New Customer</p>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setQuickCustomerOpen(false)}
                              className="h-6 w-6 p-0"
                            >
                              ×
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Name *</Label>
                              <Input
                                data-testid="quick-customer-name"
                                placeholder="Customer Name"
                                value={quickCustomerForm.customer_name}
                                onChange={(e) => setQuickCustomerForm({ ...quickCustomerForm, customer_name: e.target.value })}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Mobile *</Label>
                              <Input
                                data-testid="quick-customer-mobile"
                                placeholder="9876543210"
                                value={quickCustomerForm.mobile_number}
                                onChange={(e) => setQuickCustomerForm({ ...quickCustomerForm, mobile_number: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Address</Label>
                            <Input
                              data-testid="quick-customer-address"
                              placeholder="Full Address"
                              value={quickCustomerForm.address}
                              onChange={(e) => setQuickCustomerForm({ ...quickCustomerForm, address: e.target.value })}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Relation</Label>
                              <select
                                data-testid="quick-customer-relation"
                                className="w-full h-9 px-3 rounded-md border border-slate-200 text-sm"
                                value={quickCustomerForm.relation_type}
                                onChange={(e) => setQuickCustomerForm({ ...quickCustomerForm, relation_type: e.target.value })}
                              >
                                <option value="">Select</option>
                                {relationTypes.map(r => (
                                  <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Guardian Name</Label>
                              <Input
                                data-testid="quick-customer-relation-name"
                                placeholder="Father/Husband name"
                                value={quickCustomerForm.relation_name}
                                onChange={(e) => setQuickCustomerForm({ ...quickCustomerForm, relation_name: e.target.value })}
                              />
                            </div>
                          </div>
                          <Button
                            type="button"
                            onClick={handleQuickCustomerSubmit}
                            disabled={loading}
                            className="w-full bg-blue-600 hover:bg-blue-700 h-9"
                          >
                            {loading ? 'Adding...' : 'Add & Select Customer'}
                          </Button>
                        </div>
                      )}

                      {/* Property Selection */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Home size={16} className="text-emerald-600" />
                          Select Property *
                        </Label>
                        <select
                          data-testid="property-select"
                          value={formData.property_id}
                          onChange={(e) => handlePropertySelect(e.target.value)}
                          required
                          className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="">Choose Property</option>
                          {properties.map(prop => (
                            <option key={prop.id} value={prop.id}>
                              {prop.property_name} - {prop.size}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Auto-filled Price */}
                      {selectedProperty && (
                        <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                          <p className="text-sm text-emerald-700 font-medium">Selected Property</p>
                          <p className="text-lg font-bold text-emerald-800">{selectedProperty.property_name}</p>
                          <p className="text-sm text-emerald-600">Size: {selectedProperty.size}</p>
                          <p className="text-2xl font-bold text-emerald-700 mt-2">
                            Total Price: ₹{selectedProperty.total_price.toLocaleString('en-IN')}
                          </p>
                        </div>
                      )}

                      {/* Plot No. Allotment */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <MapPin size={16} className="text-orange-600" />
                          Plot No. / Allotment No.
                        </Label>
                        <Input
                          value={formData.plot_no || ''}
                          onChange={e => setFormData({ ...formData, plot_no: e.target.value })}
                          placeholder="e.g., A-101, Plot 25, etc."
                          className="text-sm"
                          data-testid="booking-plot-no"
                        />
                      </div>

                      {/* Agent Selection */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <UserCheck size={16} className="text-blue-600" />
                          Sales Agent
                        </Label>
                        <select
                          value={formData.agent_id || ''}
                          onChange={e => setFormData({ ...formData, agent_id: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          data-testid="booking-agent-select"
                        >
                          <option value="">-- No Agent --</option>
                          {agentsList.filter(a => a.status === 'active').map(a => (
                            <option key={a.id} value={a.id}>{a.name}{a.area ? ` (${a.area})` : ''}</option>
                          ))}
                        </select>
                      </div>

                      {/* Payment Type Selection */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <IndianRupee size={16} className="text-emerald-600" />
                          Payment Type *
                        </Label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            data-testid="full-payment-btn"
                            onClick={() => setFormData({ ...formData, payment_type: 'Full Payment', installments: [] })}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${
                              formData.payment_type === 'Full Payment'
                                ? 'border-emerald-600 bg-emerald-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <p className="font-semibold text-slate-800">Full Payment</p>
                            <p className="text-xs text-slate-500 mt-1">Pay complete amount now</p>
                          </button>
                          <button
                            type="button"
                            data-testid="installment-payment-btn"
                            onClick={() => setFormData({ ...formData, payment_type: 'Installment' })}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${
                              formData.payment_type === 'Installment'
                                ? 'border-emerald-600 bg-emerald-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <p className="font-semibold text-slate-800">Installment</p>
                            <p className="text-xs text-slate-500 mt-1">Pay in multiple installments</p>
                          </button>
                        </div>
                      </div>

                      {/* Installment Form */}
                      {formData.payment_type === 'Installment' && (
                        <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
                          {/* Quick Plan Selection */}
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                                <FileText size={16} className="text-emerald-600" />
                                Select Installment Plan
                              </h4>
                              <span className="text-xs text-slate-500">or add manually below</span>
                            </div>
                            
                            {installmentPlans.length > 0 && (
                              <div className="grid grid-cols-2 gap-2">
                                {installmentPlans.map(plan => (
                                  <button
                                    key={plan.id}
                                    type="button"
                                    data-testid={`plan-${plan.id}`}
                                    onClick={() => handlePlanSelect(plan.id)}
                                    disabled={!selectedProperty}
                                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                                      selectedPlan?.id === plan.id
                                        ? 'border-emerald-600 bg-emerald-50'
                                        : 'border-slate-200 hover:border-slate-300'
                                    } ${!selectedProperty ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  >
                                    <p className="font-medium text-sm text-slate-800">{plan.plan_name}</p>
                                    <p className="text-xs text-slate-500 mt-1">{plan.description}</p>
                                  </button>
                                ))}
                              </div>
                            )}
                            
                            {!selectedProperty && (
                              <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                                Please select a property first to use installment plans
                              </p>
                            )}
                          </div>

                          <div className="border-t border-slate-200 pt-4">
                            <h5 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                              <Edit2 size={14} />
                              Manual Add / Edit Installments
                            </h5>
                            <div className="grid grid-cols-3 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Installment Name</Label>
                                <Input
                                  data-testid="installment-name-input"
                                  placeholder="e.g., Booking Amount"
                                  value={installmentForm.installment_name}
                                  onChange={(e) => setInstallmentForm({ ...installmentForm, installment_name: e.target.value })}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Amount (₹)</Label>
                                <Input
                                  data-testid="installment-amount-input"
                                  type="number"
                                  placeholder="0"
                                  value={installmentForm.amount}
                                  onChange={(e) => setInstallmentForm({ ...installmentForm, amount: e.target.value })}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Due Date</Label>
                                <Input
                                  data-testid="installment-date-input"
                                  type="date"
                                  value={installmentForm.due_date}
                                  onChange={(e) => setInstallmentForm({ ...installmentForm, due_date: e.target.value })}
                                />
                              </div>
                            </div>
                            
                            <Button
                              type="button"
                              variant="outline"
                              data-testid="add-installment-btn"
                              onClick={handleAddInstallment}
                              className="w-full gap-2 mt-3"
                            >
                              <Plus size={16} />
                              Add Installment
                            </Button>
                          </div>

                          {/* Installment List */}
                          {formData.installments.length > 0 && (
                            <div className="space-y-2 mt-4">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-slate-700">Added Installments</p>
                                <p className="text-sm font-bold text-emerald-600">
                                  Total: ₹{totalInstallmentAmount.toLocaleString('en-IN')}
                                </p>
                              </div>
                              {formData.installments.map((inst, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                                  <div>
                                    <p className="font-medium text-slate-800">{inst.installment_name}</p>
                                    <p className="text-xs text-slate-500">
                                      Due: {new Date(inst.due_date).toLocaleDateString('en-IN')}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <p className="font-bold text-emerald-600">₹{inst.amount.toLocaleString('en-IN')}</p>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => handleRemoveInstallment(index)}
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <Button
                        type="submit"
                        data-testid="submit-booking-btn"
                        disabled={loading}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base"
                      >
                        {loading ? 'Creating...' : 'Create Booking'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="billing-stats-bar">
          {(() => {
            const totalBookings = bookings.length;
            const totalRevenue = bookings.reduce((s, b) => s + (b.total_price || 0), 0);
            const totalPaid = bookings.reduce((s, b) => s + (b.total_paid || 0), 0);
            const totalPending = totalRevenue - totalPaid;
            return [
              { label: 'Total Bookings', value: totalBookings, icon: FileText, color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
              { label: 'Total Revenue', value: `₹${totalRevenue.toLocaleString('en-IN')}`, icon: IndianRupee, color: '#059669', bg: '#f0fdf4', border: '#bbf7d0' },
              { label: 'Collected', value: `₹${totalPaid.toLocaleString('en-IN')}`, icon: CheckCircle, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
              { label: 'Pending', value: `₹${totalPending.toLocaleString('en-IN')}`, icon: Clock, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
            ];
          })().map((s, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl border" style={{ background: s.bg, borderColor: s.border }} data-testid={`billing-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
              <div className="p-2 rounded-lg shrink-0" style={{ background: s.color }}>
                <s.icon size={16} className="text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-500 font-medium truncate">{s.label}</p>
                <p className="text-sm md:text-lg font-bold truncate" style={{ color: '#0B1120' }}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-grid">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="gap-2">
              <Receipt size={16} />
              All Bookings
            </TabsTrigger>
            <TabsTrigger value="reminders" data-testid="tab-reminders" className="gap-2 relative">
              <Bell size={16} />
              Reminders
              {totalPendingReminders > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {totalPendingReminders}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="mt-4">
            <Card data-testid="bookings-table-card">
              <CardHeader>
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <CardTitle>All Bookings</CardTitle>
                  
                  {/* Search and Filter */}
                  <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                    {/* Search */}
                    <div className="relative flex-1 sm:w-64">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        data-testid="billing-search-input"
                        placeholder="Search customer, booking#, property..."
                        value={billingSearch}
                        onChange={(e) => setBillingSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    
                    {/* Month Filter */}
                    <div className="flex items-center gap-2">
                      <Filter size={16} className="text-slate-400" />
                      <select
                        data-testid="billing-month-filter"
                        value={billingMonthFilter}
                        onChange={(e) => setBillingMonthFilter(e.target.value)}
                        className="h-10 px-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      >
                        <option value="all">All Months</option>
                        {getAvailableMonths().map(month => {
                          const [year, m] = month.split('-');
                          const date = new Date(year, parseInt(m) - 1);
                          return (
                            <option key={month} value={month}>
                              {date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {/* Plot No. Filter */}
                    <div className="relative sm:w-40">
                      <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        data-testid="plot-filter"
                        placeholder="Plot No. filter"
                        value={plotFilter}
                        onChange={(e) => setPlotFilter(e.target.value)}
                        className="pl-9 h-10 text-sm"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Results count */}
                {(billingSearch || billingMonthFilter !== 'all') && (
                  <p className="text-sm text-slate-500 mt-2">
                    Showing {filteredBookings.length} of {bookings.length} bookings
                    {billingSearch && <span className="text-emerald-600"> matching "{billingSearch}"</span>}
                  </p>
                )}
              </CardHeader>
              <CardContent>
                {/* Bulk Delete Toolbar */}
                {(user?.role === 'admin' || user?.role === 'billing') && selectedBookingIds.size > 0 && (
                  <div className="flex items-center justify-between mb-4 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="bulk-delete-bookings-toolbar">
                    <span className="text-sm font-medium text-red-700">
                      {selectedBookingIds.size} selected
                    </span>
                    <Button
                      size="sm"
                      variant="destructive"
                      data-testid="bulk-delete-bookings-btn"
                      onClick={handleBulkDeleteBookings}
                      disabled={bulkDeleting}
                      className="gap-2"
                    >
                      <Trash2 size={14} />
                      {bulkDeleting ? 'Deleting...' : `Delete ${selectedBookingIds.size} Selected`}
                    </Button>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {(user?.role === 'admin' || user?.role === 'billing') && (
                          <TableHead className="w-10">
                            <input
                              type="checkbox"
                              data-testid="select-all-bookings"
                              checked={filteredBookings.length > 0 && selectedBookingIds.size === filteredBookings.length}
                              onChange={toggleSelectAllBookings}
                              className="rounded border-slate-300 accent-emerald-600 w-4 h-4"
                            />
                          </TableHead>
                        )}
                        <TableHead>Booking #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Guardian/Relation</TableHead>
                        <TableHead>Property</TableHead>
                        <TableHead>Plot No.</TableHead>
                        <TableHead>Total Price</TableHead>
                        <TableHead>Payment Type</TableHead>
                        <TableHead>Paid</TableHead>
                        <TableHead>Balance</TableHead>
                        <TableHead>Agent</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isDataLoading ? (
                        <TableRow>
                          <TableCell colSpan={(user?.role === 'admin' || user?.role === 'billing') ? 13 : 12} className="text-center py-8">
                            <div className="flex items-center justify-center gap-2 text-slate-500">
                              <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                              Loading bookings...
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : filteredBookings.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={(user?.role === 'admin' || user?.role === 'billing') ? 11 : 10} className="text-center text-slate-500 py-8">
                            {bookings.length === 0 
                              ? "No bookings found. Create your first booking!"
                              : "No bookings match your search criteria."
                            }
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredBookings.map((booking) => (
                          <TableRow key={booking.id} data-testid={`booking-row-${booking.id}`} className={`hover:bg-slate-50 ${selectedBookingIds.has(booking.id) ? 'bg-emerald-50' : ''}`}>
                            {(user?.role === 'admin' || user?.role === 'billing') && (
                              <TableCell>
                                <input
                                  type="checkbox"
                                  data-testid={`select-booking-${booking.id}`}
                                  checked={selectedBookingIds.has(booking.id)}
                                  onChange={() => toggleBookingSelect(booking.id)}
                                  className="rounded border-slate-300 accent-emerald-600 w-4 h-4"
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <div>
                                <p className="font-mono text-sm font-semibold">{booking.booking_number}</p>
                                <p className="text-xs text-slate-400">
                                  {new Date(booking.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{booking.customer_name}</p>
                                <p className="text-xs text-slate-500">{booking.mobile_number}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                {booking.customer_relation_type || booking.relation_type ? (
                                  <>
                                    <p className="text-xs text-slate-500 capitalize">{booking.customer_relation_type || booking.relation_type}</p>
                                    <p className="font-medium text-sm">{booking.customer_relation_name || booking.relation_name || '-'}</p>
                                  </>
                                ) : (
                                  <span className="text-slate-400 text-xs">-</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{booking.property_name}</p>
                                <p className="text-xs text-slate-500">{booking.property_size}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              {booking.plot_no ? (
                                <span className="font-semibold text-orange-700 bg-orange-50 px-2 py-0.5 rounded text-xs border border-orange-200">{booking.plot_no}</span>
                              ) : (
                                <span className="text-slate-300 text-xs">-</span>
                              )}
                            </TableCell>
                            <TableCell className="font-semibold">₹{booking.total_price.toLocaleString('en-IN')}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {booking.payment_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-green-600 font-semibold">
                              ₹{booking.total_paid.toLocaleString('en-IN')}
                            </TableCell>
                            <TableCell className="text-red-600 font-semibold">
                              ₹{booking.balance_due.toLocaleString('en-IN')}
                            </TableCell>
                            <TableCell>
                              {booking.agent_name ? (
                                <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{booking.agent_name}</span>
                              ) : (
                                <select
                                  value=""
                                  onChange={async (e) => {
                                    if (!e.target.value) return;
                                    try {
                                      await api.patch(`/bookings/${booking.id}/assign-agent`, { agent_id: e.target.value });
                                      toast.success('Agent assigned!');
                                      fetchData();
                                    } catch { toast.error('Assign failed'); }
                                  }}
                                  className="text-xs border border-slate-200 rounded px-1 py-0.5 text-slate-400"
                                  data-testid={`assign-agent-${booking.id}`}
                                >
                                  <option value="">Assign</option>
                                  {agentsList.filter(a => a.status === 'active').map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                              )}
                            </TableCell>
                            <TableCell>{getStatusBadge(booking.payment_status)}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  data-testid={`view-booking-${booking.id}`}
                                  onClick={() => setViewBooking(booking)}
                                  className="gap-1"
                                >
                                  <Eye size={14} />
                                  View
                                </Button>
                                {booking.balance_due > 0 && booking.payment_type === 'Installment' && (
                                  <Button
                                    size="sm"
                                    data-testid={`pay-booking-${booking.id}`}
                                    onClick={() => openPaymentDialog(booking)}
                                    className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                                  >
                                    <CreditCard size={14} />
                                    Pay
                                  </Button>
                                )}
                                {(user?.role === 'admin' || user?.role === 'billing') && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    data-testid={`edit-booking-${booking.id}`}
                                    onClick={() => openEditBooking(booking)}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 gap-1"
                                  >
                                    <Edit2 size={14} /> Edit
                                  </Button>
                                )}
                                {(user?.role === 'admin' || user?.role === 'billing') && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    data-testid={`delete-booking-${booking.id}`}
                                    onClick={() => handleDeleteBooking(booking.id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reminders Tab */}
          <TabsContent value="reminders" className="mt-4 space-y-6">
            {/* Overdue */}
            {reminders.overdue.length > 0 && (
              <Card className="border-red-200 bg-red-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-red-700 flex items-center gap-2">
                    <AlertCircle size={20} />
                    Overdue ({reminders.overdue.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {reminders.overdue.map((item, idx) => (
                    <ReminderCard key={idx} item={item} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Due Today */}
            {reminders.dueToday.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-amber-700 flex items-center gap-2">
                    <CalendarClock size={20} />
                    Due Today ({reminders.dueToday.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {reminders.dueToday.map((item, idx) => (
                    <ReminderCard key={idx} item={item} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Due This Week */}
            {reminders.dueThisWeek.length > 0 && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-blue-700 flex items-center gap-2">
                    <Calendar size={20} />
                    Due This Week ({reminders.dueThisWeek.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {reminders.dueThisWeek.map((item, idx) => (
                    <ReminderCard key={idx} item={item} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Due This Month */}
            {reminders.dueThisMonth.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-slate-700 flex items-center gap-2">
                    <Calendar size={20} />
                    Due This Month ({reminders.dueThisMonth.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {reminders.dueThisMonth.map((item, idx) => (
                    <ReminderCard key={idx} item={item} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Due Next Month */}
            {reminders.dueNextMonth.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-slate-600 flex items-center gap-2">
                    <Calendar size={20} />
                    Due Next Month ({reminders.dueNextMonth.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {reminders.dueNextMonth.map((item, idx) => (
                    <ReminderCard key={idx} item={item} showMarkPaid={false} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* No Reminders */}
            {totalPendingReminders === 0 && reminders.dueThisMonth.length === 0 && reminders.dueNextMonth.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle size={48} className="mx-auto text-emerald-500 mb-4" />
                  <p className="text-lg font-semibold text-slate-800">All Clear!</p>
                  <p className="text-slate-500">No upcoming payment reminders</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* View Booking Dialog - Premium Invoice */}
        <Dialog open={!!viewBooking} onOpenChange={(open) => !open && setViewBooking(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="invoice-dialog">
            <DialogHeader className="p-5 pb-0">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-lg">Invoice Preview</DialogTitle>
                <Button onClick={handlePrint} data-testid="print-invoice-button" className="gap-2 bg-[#0B1120] hover:bg-[#1a2744] text-[#C9A84C]">
                  <Printer size={16} />
                  Print Invoice
                </Button>
              </div>
            </DialogHeader>
            
            {viewBooking && (
              <div ref={printRef} className="bg-white">
                <div className="px-6 pb-6 space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between pb-3 border-b-[3px] border-[#0B1120]">
                    <div className="flex items-center gap-3">
                      <img src="/logo.png" alt="Logo" className="h-12 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
                      <div>
                        <h1 className="text-xl font-extrabold text-[#0B1120] leading-tight">
                          {billingSettings?.company_name || 'Ganesa Homestate Pvt. Ltd.'}
                        </h1>
                        <p className="text-[10px] text-[#C9A84C] font-bold tracking-[2px] uppercase">
                          {billingSettings?.company_tagline || 'Tradition of Trust'}
                        </p>
                        {billingSettings?.address_line1 && (
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            {billingSettings.address_line1}{billingSettings.city && `, ${billingSettings.city}`}{billingSettings.state && `, ${billingSettings.state}`}{billingSettings.pincode && ` - ${billingSettings.pincode}`}
                          </p>
                        )}
                        <div className="flex items-center gap-3 text-[9px] text-slate-400 mt-0.5">
                          {billingSettings?.phone && <span>Ph: {billingSettings.phone}</span>}
                          {billingSettings?.email && <span>{billingSettings.email}</span>}
                          {billingSettings?.website && <span>{billingSettings.website}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-black text-[#0B1120] tracking-[4px] leading-none">INVOICE</p>
                      <p className="text-sm font-bold text-[#C9A84C] font-mono mt-1">{viewBooking.booking_number}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {new Date(viewBooking.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                      {billingSettings?.gst_number && (
                        <p className="text-[9px] text-slate-400 font-mono">GSTIN: {billingSettings.gst_number}</p>
                      )}
                    </div>
                  </div>
                  <div className="h-[3px] bg-gradient-to-r from-[#C9A84C] via-[#e8d48b] to-[#C9A84C] -mt-1"></div>

                  {/* Details Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 border border-slate-200 rounded-md">
                      <p className="text-[9px] font-bold text-[#C9A84C] uppercase tracking-[1.5px] mb-2">Bill To</p>
                      <p className="text-base font-bold text-[#0B1120]">{viewBooking.customer_name}</p>
                      {viewBooking.relation_type && viewBooking.relation_name && (
                        <p className="text-xs text-slate-500">
                          {viewBooking.relation_type === 'father' ? 'S/o' : viewBooking.relation_type === 'husband' ? 'W/o' : viewBooking.relation_type === 'c/o' ? 'C/o' : 'R/o'} {viewBooking.relation_name}
                        </p>
                      )}
                      <p className="text-xs text-slate-500">{viewBooking.mobile_number}</p>
                      {viewBooking.address && <p className="text-xs text-slate-500">{viewBooking.address}</p>}
                    </div>
                    <div className="p-3 bg-[#fafaf5] border border-[#d4c88a] rounded-md">
                      <p className="text-[9px] font-bold text-[#C9A84C] uppercase tracking-[1.5px] mb-2">Property Details</p>
                      <p className="text-base font-bold text-[#0B1120]">{viewBooking.property_name}</p>
                      <p className="text-xs text-slate-500">Size: {viewBooking.property_size || '-'}</p>
                      {viewBooking.plot_no && (
                        <p className="text-sm font-bold text-orange-700 mt-1 bg-orange-50 inline-block px-2 py-0.5 rounded border border-orange-200">Plot No: {viewBooking.plot_no}</p>
                      )}
                      <p className="text-lg font-extrabold text-[#0B1120] mt-1">
                        ₹{viewBooking.total_price.toLocaleString('en-IN')}
                        <span className="text-[10px] text-slate-400 font-normal ml-1">Total Value</span>
                      </p>
                    </div>
                  </div>

                  {/* Summary Bar */}
                  <div className="flex border border-slate-200 rounded-md overflow-hidden">
                    <div className="flex-1 p-2 text-center bg-slate-50 border-r border-slate-200">
                      <p className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">Payment Type</p>
                      <p className="text-sm font-bold text-[#0B1120]">{viewBooking.payment_type}</p>
                    </div>
                    <div className="flex-1 p-2 text-center bg-green-50 border-r border-slate-200">
                      <p className="text-[8px] font-bold text-green-600 uppercase tracking-wider">Amount Paid</p>
                      <p className="text-sm font-extrabold text-green-600">₹{viewBooking.total_paid.toLocaleString('en-IN')}</p>
                    </div>
                    <div className="flex-1 p-2 text-center bg-red-50 border-r border-slate-200">
                      <p className="text-[8px] font-bold text-red-600 uppercase tracking-wider">Balance Due</p>
                      <p className="text-sm font-extrabold text-red-600">₹{viewBooking.balance_due.toLocaleString('en-IN')}</p>
                    </div>
                    <div className="flex-1 p-2 text-center bg-amber-50">
                      <p className="text-[8px] font-bold text-amber-700 uppercase tracking-wider">Progress</p>
                      <p className="text-sm font-extrabold text-amber-700">
                        {viewBooking.total_price > 0 ? Math.round((viewBooking.total_paid / viewBooking.total_price) * 100) : 0}%
                      </p>
                    </div>
                  </div>

                  {/* Agent Assignment */}
                  <div className="flex items-center gap-3 p-2.5 bg-blue-50/50 border border-blue-100 rounded-md">
                    <UserCheck size={14} className="text-blue-600 shrink-0" />
                    <span className="text-xs font-semibold text-slate-600">Sales Agent:</span>
                    {viewBooking.agent_name ? (
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-sm font-bold text-blue-700">{viewBooking.agent_name}</span>
                        <button onClick={async () => {
                          try { await api.patch(`/bookings/${viewBooking.id}/assign-agent`, { agent_id: '' }); toast.success('Agent removed'); const res = await api.get(`/bookings`); setBookings(res.data); setViewBooking(prev => ({ ...prev, agent_id: null, agent_name: null })); } catch { toast.error('Failed'); }
                        }} className="text-xs text-red-400 hover:text-red-600">Remove</button>
                      </div>
                    ) : (
                      <select onChange={async (e) => {
                        if (!e.target.value) return;
                        try {
                          await api.patch(`/bookings/${viewBooking.id}/assign-agent`, { agent_id: e.target.value });
                          const agent = agentsList.find(a => a.id === e.target.value);
                          toast.success(`Agent ${agent?.name} assigned!`);
                          const res = await api.get(`/bookings`); setBookings(res.data);
                          setViewBooking(prev => ({ ...prev, agent_id: e.target.value, agent_name: agent?.name || '' }));
                        } catch { toast.error('Assign failed'); }
                      }} className="text-xs border border-blue-200 rounded px-2 py-1 bg-white" data-testid="detail-assign-agent">
                        <option value="">-- Select Agent --</option>
                        {agentsList.filter(a => a.status === 'active').map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    )}
                  </div>

                  {/* Full Payment Stamp */}
                  {viewBooking.payment_type === 'Full Payment' && (
                    <div className="text-center py-3">
                      <span className="inline-block w-20 h-20 border-[3px] border-green-600 rounded-full leading-[74px] text-lg font-black text-green-600 tracking-wider -rotate-12 opacity-60">PAID</span>
                    </div>
                  )}

                  {/* Installment Schedule */}
                  {viewBooking.installments && viewBooking.installments.length > 0 && (
                    <div className="overflow-hidden rounded-md border border-slate-200">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-[#0B1120]">
                            <th className="text-left py-2 px-3 text-[9px] font-bold text-[#C9A84C] uppercase tracking-wider">Installment</th>
                            <th className="text-right py-2 px-3 text-[9px] font-bold text-[#C9A84C] uppercase tracking-wider">Amount</th>
                            <th className="text-right py-2 px-3 text-[9px] font-bold text-[#C9A84C] uppercase tracking-wider">Paid</th>
                            <th className="text-right py-2 px-3 text-[9px] font-bold text-[#C9A84C] uppercase tracking-wider">Remaining</th>
                            <th className="text-center py-2 px-3 text-[9px] font-bold text-[#C9A84C] uppercase tracking-wider">Due Date</th>
                            <th className="text-center py-2 px-3 text-[9px] font-bold text-[#C9A84C] uppercase tracking-wider">Paid Date</th>
                            <th className="text-center py-2 px-3 text-[9px] font-bold text-[#C9A84C] uppercase tracking-wider">Status</th>
                            <th className="text-center py-2 px-3 text-[9px] font-bold text-[#C9A84C] uppercase tracking-wider print:hidden">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewBooking.installments.map((inst, index) => {
                            const paidAmt = inst.paid_amount || 0;
                            const remaining = Math.max(0, (inst.amount || 0) - paidAmt);
                            const hasHistory = inst.payment_history && inst.payment_history.length > 0;
                            return (
                              <React.Fragment key={index}>
                                <tr className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                  <td className="py-2 px-3 text-xs font-semibold text-slate-800">
                                    {inst.installment_name}
                                    {hasHistory && (
                                      <button
                                        data-testid={`toggle-history-${index}`}
                                        onClick={() => setExpandedInstallment(expandedInstallment === `${viewBooking.id}-${index}` ? null : `${viewBooking.id}-${index}`)}
                                        className="ml-1 text-[10px] text-blue-600 hover:underline"
                                      >
                                        ({inst.payment_history.length} payments)
                                      </button>
                                    )}
                                  </td>
                                  <td className="py-2 px-3 text-right text-xs font-bold text-slate-800">
                                    ₹{(inst.amount || 0).toLocaleString('en-IN')}
                                  </td>
                                  <td className="py-2 px-3 text-right text-xs font-bold text-green-600">
                                    ₹{paidAmt.toLocaleString('en-IN')}
                                  </td>
                                  <td className={`py-2 px-3 text-right text-xs font-bold ${remaining > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                                    ₹{remaining.toLocaleString('en-IN')}
                                  </td>
                                  <td className="py-2 px-3 text-center text-xs text-slate-500">
                                    {inst.due_date ? new Date(inst.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                  </td>
                                  <td className="py-2 px-3 text-center text-xs text-green-600 font-medium">
                                    {(() => {
                                      const lastPh = inst.payment_history && inst.payment_history.length > 0 ? inst.payment_history[inst.payment_history.length - 1] : null;
                                      const paidDt = inst.paid_date || (lastPh ? lastPh.payment_date : null);
                                      return paidDt ? new Date(paidDt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
                                    })()}
                                  </td>
                                  <td className="py-2 px-3 text-center">
                                    <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-bold ${
                                      inst.status === 'Paid' ? 'bg-green-100 text-green-700'
                                      : inst.status === 'Partially Paid' ? 'bg-blue-100 text-blue-700'
                                      : 'bg-amber-100 text-amber-700'
                                    }`}>
                                      {inst.status === 'Paid' ? 'PAID' : inst.status === 'Partially Paid' ? 'PARTIAL' : 'PENDING'}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-center print:hidden">
                                    {inst.status !== 'Paid' && (
                                      <Button
                                        size="sm"
                                        data-testid={`record-payment-${index}`}
                                        onClick={() => {
                                          openPaymentDialog(viewBooking);
                                          setTimeout(() => {
                                            setPaymentForm(prev => ({
                                              ...prev,
                                              installment_name: inst.installment_name,
                                              custom_amount: String(remaining),
                                              use_custom_amount: false
                                            }));
                                          }, 100);
                                        }}
                                        className="bg-[#0B1120] hover:bg-[#1a2744] gap-1 h-7 text-[10px] px-2"
                                      >
                                        <CreditCard size={12} />
                                        {inst.status === 'Partially Paid' ? 'Pay More' : 'Pay'}
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                                {/* Payment History */}
                                {hasHistory && expandedInstallment === `${viewBooking.id}-${index}` && (
                                  <tr>
                                    <td colSpan="8" className="px-3 py-2 bg-blue-50">
                                      <div className="space-y-1">
                                        <p className="text-[10px] font-semibold text-blue-700 mb-1">Payment History</p>
                                        {inst.payment_history.map((ph, phIdx) => (
                                          <div key={phIdx} className="flex items-center justify-between text-[10px] bg-white rounded px-2 py-1 border border-blue-100">
                                            <div className="flex items-center gap-2">
                                              <span className="font-bold text-green-600">₹{(ph.amount || 0).toLocaleString('en-IN')}</span>
                                              <span className="text-slate-500">{ph.payment_mode || 'Cash'}</span>
                                              {ph.notes && <span className="text-slate-400 italic">{ph.notes}</span>}
                                            </div>
                                            <div className="flex items-center gap-2 text-slate-500">
                                              <span>{ph.recorded_by}</span>
                                              <span>{ph.payment_date ? new Date(ph.payment_date).toLocaleDateString('en-IN') : '-'}</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="bg-slate-100 font-bold border-t-2 border-[#0B1120]">
                            <td className="py-2 px-3 text-xs">TOTAL</td>
                            <td className="py-2 px-3 text-right text-xs">₹{viewBooking.total_price.toLocaleString('en-IN')}</td>
                            <td className="py-2 px-3 text-right text-xs text-green-700">₹{viewBooking.total_paid.toLocaleString('en-IN')}</td>
                            <td className="py-2 px-3 text-right text-xs text-red-700">₹{viewBooking.balance_due.toLocaleString('en-IN')}</td>
                            <td colSpan="4"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}

                  {/* Bank Details */}
                  {viewBooking.balance_due > 0 && billingSettings?.bank_name && (
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                      <span className="text-[8px] font-bold text-[#C9A84C] uppercase tracking-wider whitespace-nowrap">Payment Info</span>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-700">
                        {billingSettings.bank_name && <span><b className="text-slate-500">Bank:</b> {billingSettings.bank_name}</span>}
                        {billingSettings.account_number && <span><b className="text-slate-500">A/C:</b> {billingSettings.account_number}</span>}
                        {billingSettings.ifsc_code && <span><b className="text-slate-500">IFSC:</b> {billingSettings.ifsc_code}</span>}
                        {billingSettings.upi_id && <span><b className="text-slate-500">UPI:</b> {billingSettings.upi_id}</span>}
                      </div>
                    </div>
                  )}

                  {/* Terms */}
                  {billingSettings?.terms_and_conditions && (
                    <p className="text-[9px] text-slate-400">
                      <b className="text-slate-500">Terms:</b> {billingSettings.terms_and_conditions}
                    </p>
                  )}

                  {/* Footer */}
                  <div className="bg-[#0B1120] rounded-md px-4 py-2 flex items-center justify-between mt-2">
                    <div>
                      <p className="text-[#C9A84C] text-xs font-bold">{billingSettings?.company_name || 'Ganesa Homestate Pvt. Ltd.'}</p>
                      <p className="text-slate-500 text-[9px]">{billingSettings?.company_tagline || 'Tradition of Trust'}</p>
                    </div>
                    <div className="text-right text-[9px] text-slate-500">
                      <p>Thank you for your trust!</p>
                      <p>Computer generated invoice</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Import Results Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="import-results-dialog">
            <DialogHeader>
              <DialogTitle>Import Results</DialogTitle>
            </DialogHeader>
            {importResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-blue-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{importResult.total_rows}</p>
                    <p className="text-xs text-slate-500">Total Rows</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-600">{importResult.bookings_created}</p>
                    <p className="text-xs text-slate-500">Bookings Created</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-purple-600">{importResult.customers_created}</p>
                    <p className="text-xs text-slate-500">New Customers</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-amber-600">{importResult.properties_created}</p>
                    <p className="text-xs text-slate-500">New Properties</p>
                  </div>
                </div>

                {importResult.errors?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                    <p className="text-sm font-medium text-red-700 mb-2">Errors ({importResult.errors.length}):</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {importResult.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-600">Row {err.row}: {err.error}</p>
                      ))}
                    </div>
                  </div>
                )}

                {importResult.imported?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Imported Bookings:</p>
                    <div className="overflow-x-auto max-h-60 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Booking #</TableHead>
                            <TableHead>Customer</TableHead>
                            <TableHead>Property</TableHead>
                            <TableHead>Total</TableHead>
                            <TableHead>Paid</TableHead>
                            <TableHead>Balance</TableHead>
                            <TableHead>Installments</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importResult.imported.map((item, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-xs font-mono">{item.booking_number}</TableCell>
                              <TableCell className="text-xs">{item.customer}</TableCell>
                              <TableCell className="text-xs">{item.property}</TableCell>
                              <TableCell className="text-xs font-semibold">₹{item.total?.toLocaleString('en-IN')}</TableCell>
                              <TableCell className="text-xs text-emerald-600">₹{item.paid?.toLocaleString('en-IN')}</TableCell>
                              <TableCell className="text-xs text-red-600">₹{item.balance?.toLocaleString('en-IN')}</TableCell>
                              <TableCell className="text-xs">{item.installments}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Booking Dialog */}
      <Dialog open={!!editBookingDialog} onOpenChange={() => setEditBookingDialog(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 size={18} className="text-blue-600" /> Edit Booking
              {editBookingDialog && <span className="text-xs text-slate-400 font-normal ml-2">#{editBookingDialog.booking_number}</span>}
            </DialogTitle>
          </DialogHeader>
          {editBookingDialog && (
            <div className="space-y-3" data-testid="edit-booking-form">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold">Customer Name</Label>
                  <Input value={editBookingForm.customer_name} onChange={e => setEditBookingForm({...editBookingForm, customer_name: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-bold">Mobile Number</Label>
                  <Input value={editBookingForm.mobile_number} onChange={e => setEditBookingForm({...editBookingForm, mobile_number: e.target.value})} className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold">Address</Label>
                <Input value={editBookingForm.address} onChange={e => setEditBookingForm({...editBookingForm, address: e.target.value})} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold">Relation Type</Label>
                  <select value={editBookingForm.relation_type} onChange={e => setEditBookingForm({...editBookingForm, relation_type: e.target.value})} className="w-full h-9 px-3 text-sm border rounded-md bg-white mt-1">
                    <option value="">None</option>
                    <option value="S/O">S/O (Son of)</option><option value="D/O">D/O (Daughter of)</option>
                    <option value="W/O">W/O (Wife of)</option><option value="H/O">H/O (Husband of)</option>
                    <option value="C/O">C/O (Care of)</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-bold">Relation Name</Label>
                  <Input value={editBookingForm.relation_name} onChange={e => setEditBookingForm({...editBookingForm, relation_name: e.target.value})} className="mt-1" />
                </div>
              </div>

              <hr className="my-1" />

              {/* Property Info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold">Property</Label>
                  <select value={editBookingForm.property_id} onChange={e => { const p = properties.find(pr => pr.id === e.target.value); setEditBookingForm({...editBookingForm, property_id: e.target.value, property_name: p?.property_name || editBookingForm.property_name}); }} className="w-full h-9 px-3 text-sm border rounded-md bg-white mt-1">
                    <option value="">-- Select --</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.property_name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-bold">Property Size</Label>
                  <Input value={editBookingForm.property_size} onChange={e => setEditBookingForm({...editBookingForm, property_size: e.target.value})} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold flex items-center gap-1"><MapPin size={12} className="text-orange-600" /> Plot No.</Label>
                  <Input value={editBookingForm.plot_no} onChange={e => setEditBookingForm({...editBookingForm, plot_no: e.target.value})} placeholder="e.g., A-101" className="mt-1" data-testid="edit-plot-no" />
                </div>
                <div>
                  <Label className="text-xs font-bold">Total Price (₹)</Label>
                  <Input type="number" value={editBookingForm.total_price} onChange={e => setEditBookingForm({...editBookingForm, total_price: e.target.value})} className="mt-1" />
                </div>
              </div>

              <hr className="my-1" />

              {/* Agent */}
              <div>
                <Label className="text-xs font-bold">Agent</Label>
                <select value={editBookingForm.agent_id} onChange={e => { const a = agentsList.find(ag => ag.id === e.target.value); setEditBookingForm({...editBookingForm, agent_id: e.target.value, agent_name: a?.name || ''}); }} className="w-full h-9 px-3 text-sm border rounded-md bg-white mt-1">
                  <option value="">No Agent</option>
                  {agentsList.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={handleEditBooking} className="flex-1 bg-[#0B1120] hover:bg-[#1a2744] gap-1" data-testid="save-edit-booking">
                  <CheckCircle size={14} /> Save Changes
                </Button>
                <Button onClick={() => setEditBookingDialog(null)} variant="outline">Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
